// ═══════════════════════════════════════════════════════════════════
// COSTINGWORKSHEETS.JS — BASE FILE (UPGRADED)
// Adds Sourcing price panel to every costing block
// CW-SPLIT1 and CW-SPLIT2 must be pasted below
// ═══════════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';

const API_BASE = 'https://nirvikalp.integrashion.com/api';

const saveCostingToDB = async (data) => {
    try {
        const payload = { category: 'costing', data: data };
        const response = await fetch(`${API_BASE}/save_universal.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        const text = await response.text();
        if (!text) throw new Error("Empty response from server");
        const result = JSON.parse(text);
        if (!result.success) throw new Error(result.error || 'Save failed');
        alert("Costing Saved Successfully! Version: " + result.version);
        return result;
    } catch (err) {
        console.error('Costing save error:', err);
        alert('Failed to save costing: ' + err.message);
        return null;
    }
};

// ── Preserved table component ──────────────────────────────────────
const CustomTable = ({ data, cols, onDelete, onEdit, currency }) => (
    <div style={{overflowX:'auto', border:'1px solid #DEE2E6', borderRadius:'6px'}}>
        <table style={{width:'100%', borderCollapse:'collapse', fontSize:'0.9rem'}}>
            <thead>
                <tr style={{background:'#F8F9FA', borderBottom:'2px solid #DEE2E6'}}>
                    {cols.map(c => <th key={c} style={{padding:'12px 15px', textAlign:'left', textTransform:'uppercase', color:'#6C757D', fontSize:'0.75rem'}}>{c}</th>)}
                    <th style={{padding:'12px 15px', textAlign:'center', width:'100px'}}>ACTIONS</th>
                </tr>
            </thead>
            <tbody>
                {data.map((row, i) => (
                    <tr key={i} style={{borderBottom:'1px solid #F4F6F8', background: i%2===0 ? 'white' : '#fcfcfc'}}>
                        {cols.map(c => <td key={c} style={{padding:'12px 15px', color:'#212529'}}>{row[c]}</td>)}
                        <td style={{padding:'12px 15px', textAlign:'center', display:'flex', gap:'5px', justifyContent:'center'}}>
                            <button onClick={()=>onEdit(row)} style={{border:'1px solid #CED4DA', background:'white', borderRadius:'4px', padding:'4px 8px', cursor:'pointer', fontSize:'0.75rem'}}>Edit</button>
                            <button onClick={()=>onDelete(row.id)} style={{border:'none', background:'#C62828', color:'white', borderRadius:'4px', padding:'4px 8px', cursor:'pointer', fontSize:'0.75rem'}}>Del</button>
                        </td>
                    </tr>
                ))}
                {data.length === 0 && <tr><td colSpan={cols.length+1} style={{padding:'20px', textAlign:'center', color:'#6C757D'}}>No Items Added</td></tr>}
            </tbody>
        </table>
    </div>
);

// ── NEW: Sourcing Reference Prices Panel ───────────────────────────
// Reads db.ws.sourcingPrices (pushed by Sourcing module)
// blockKey: 'Fabric' | 'Trims' | 'Embellishment' | 'CM' | 'Packaging'
const SourcingRefPanel = ({ db, blockKey, conversionFactor, currencyName, onUsePrice }) => {
    const allPrices = db?.ws?.sourcingPrices || db?.sourcingPrices || [];
    // Also pull from supplierQuotations for broader coverage
    const allQuotes = db?.supplierQuotations || [];

    const BLOCK_KW = {
        Fabric:        ['fabric','woven','knit','lining','yarn','denim','twill','poplin','jersey','georgette','crepe','satin'],
        Trims:         ['trim','button','zipper','label','tag','thread','elastic','interlining','velcro','hook','snap'],
        Embellishment: ['embroid','print','wash','heat','patch','stone','sequin','va','process'],
        CM:            ['cm','cut','make','sewing','stitch','labour'],
        Packaging:     ['pack','polybag','carton','hanger','tissue','box','barcode','tag'],
    };

    const kws = BLOCK_KW[blockKey] || [];

    // Match from pushed sourcingPrices by block name
    const fromPush = allPrices.filter(p => p.block === blockKey);

    // Match from raw quotations by keyword
    const fromQuotes = allQuotes.filter(q => {
        const item = (q.item || '').toLowerCase();
        return kws.some(w => item.includes(w));
    });

    // Merge, deduplicate by id
    const seen = new Set();
    const merged = [...fromPush, ...fromQuotes].filter(p => {
        if (seen.has(p.id)) return false;
        seen.add(p.id);
        return true;
    }).sort((a,b) => parseFloat(a.price) - parseFloat(b.price));

    if (merged.length === 0) return null;

    return (
        <div style={{ marginBottom:'16px', padding:'12px 14px', background:'linear-gradient(135deg,#f0fdf4,#E3F2FD)', border:'1px solid #A5D6A7', borderRadius:'8px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'10px' }}>
                <div style={{ width:'8px', height:'8px', borderRadius:'50%', background:'#388E3C' }}/>
                <span style={{ fontSize:'11px', fontWeight:'800', color:'#15803d', textTransform:'uppercase', letterSpacing:'0.05em' }}>
                    Sourcing Reference Prices — {merged.length} quote{merged.length!==1?'s':''} available
                </span>
                <span style={{ marginLeft:'auto', fontSize:'10px', color:'#6C757D' }}>Click a price to use it</span>
            </div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:'8px' }}>
                {merged.map((p,i) => {
                    const displayPrice = (parseFloat(p.price||0) * conversionFactor).toFixed(3);
                    const isBest = i === 0;
                    return (
                        <div key={p.id||i} onClick={()=>onUsePrice(p)}
                            style={{ padding:'8px 12px', background:'white', border:`1.5px solid ${isBest?'#388E3C':'#DEE2E6'}`, borderRadius:'8px', cursor:'pointer', minWidth:'140px', position:'relative', boxShadow: isBest?'0 2px 8px rgba(22,163,74,0.15)':'none', transition:'all 0.15s' }}
                            onMouseEnter={e=>e.currentTarget.style.boxShadow='0 4px 12px rgba(0,0,0,0.1)'}
                            onMouseLeave={e=>e.currentTarget.style.boxShadow=isBest?'0 2px 8px rgba(22,163,74,0.15)':'none'}>
                            {isBest && (
                                <div style={{ position:'absolute', top:'-8px', right:'6px', background:'#388E3C', color:'white', fontSize:'8px', padding:'1px 6px', borderRadius:'8px', fontWeight:'800' }}>BEST</div>
                            )}
                            <div style={{ fontSize:'11px', fontWeight:'700', color:'#212529', marginBottom:'2px' }}>{p.item}</div>
                            <div style={{ fontSize:'10px', color:'#6C757D', marginBottom:'4px' }}>{p.supplier}</div>
                            <div style={{ fontSize:'15px', fontWeight:'800', color: isBest?'#388E3C':'#1565C0' }}>
                                {currencyName} {displayPrice}
                                <span style={{ fontSize:'9px', fontWeight:'400', color:'#6C757D', marginLeft:'3px' }}>/{p.unit||'unit'}</span>
                            </div>
                            {p.leadTime && <div style={{ fontSize:'9px', color:'#6C757D', marginTop:'2px' }}>Lead: {p.leadTime}</div>}
                            {p.moq      && <div style={{ fontSize:'9px', color:'#6C757D' }}>MOQ: {p.moq}</div>}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function CostingWorksheets({ db, ws, setWs, activeBlock, setActiveBlock, saveCosting, currentStyle, currency, openStylePassport }) {
  const [tIn, setTIn] = useState({});
  const [editId, setEditId] = useState(null);
  const [selectedZones, setSelectedZones] = useState([]); // zone IDs checked for comparison

  useEffect(() => { setTIn({}); setEditId(null); }, [activeBlock]);

  // WORKSTATION DATA LINK (preserved)
  // Must be declared BEFORE the useEffect that uses it as a dependency
  let workstationSMV = 0;
  if (currentStyle?.workstationData?.aggregatedOps && Array.isArray(currentStyle.workstationData.aggregatedOps)) {
      workstationSMV = currentStyle.workstationData.aggregatedOps.reduce((sum, op) => sum + (parseFloat(op.smv) || 0), 0);
  } else {
      workstationSMV = parseFloat(currentStyle?.workstationData?.smv || currentStyle?.smv || 0);
  }

  // Auto-populate cm.sam from workstation when workstation has data and costing field is empty
  useEffect(() => {
    if (workstationSMV > 0 && !(parseFloat(ws.cm?.sam) > 0)) {
      setWs(prev => ({ ...prev, cm: { ...prev.cm, sam: parseFloat(workstationSMV.toFixed(3)) } }));
    }
  }, [workstationSMV]); // eslint-disable-line

  // MARKER DATA LINK — same idea as the SMV bridge above: MarkerMaker's
  // "Save to Costing" button persists marker_data onto the style record
  // (real computed fabric wastage/consumption from the actual pattern
  // nesting), so this sheet doesn't have to rely on a manual guess.
  const markerData = currentStyle?.marker_data || null;

  const safeDb = {
      fabrics:         db?.fabrics || [],
      trimTypes:       db?.trimTypes || [],
      vaProcesses:     (db?.vaLibrary && db.vaLibrary.length > 0) ? db.vaLibrary : (db?.vaProcesses || []),
      uoms:            db?.uoms || [],
      productionZones: db?.productionZones || [],
      suppliers:       db?.suppliers || [],
  };

  const safeCurrency     = currency || { name: 'USD', rate: 1 };
  const baseCurrency     = db?.currencies?.find((c) => c.isBase) || { rate: 1 };
  const baseRate         = baseCurrency.rate || 1;
  const currRate         = safeCurrency.rate || 1;
  const conversionFactor = currRate / baseRate;
  const rateLabel        = `Rate (${safeCurrency.name})`;

  // ─────────────────────────────────────────────────────────────────
  // ZONE COMPARISON ENGINE
  // Formula:  CM = SMV × CPM@100% ÷ (achievableEff / 100)
  // achievableEff = factoryPeakEff × learningCurveFactor(orderQty)
  // ─────────────────────────────────────────────────────────────────
  const LC_TIERS = [
    { maxQty: 300,      factor: 0.60, label: '< 300 pcs',      color: '#C62828', bg: '#FFEBEE', barPct: 20 },
    { maxQty: 600,      factor: 0.70, label: '300–600 pcs',    color: '#f97316', bg: '#fff7ed', barPct: 35 },
    { maxQty: 1200,     factor: 0.78, label: '600–1 200 pcs',  color: '#FF8F00', bg: '#fefce8', barPct: 52 },
    { maxQty: 3000,     factor: 0.84, label: '1 200–3 000 pcs',color: '#84cc16', bg: '#f7fee7', barPct: 68 },
    { maxQty: 6000,     factor: 0.89, label: '3 000–6 000 pcs',color: '#2E7D32', bg: '#f0fdf4', barPct: 82 },
    { maxQty: Infinity, factor: 0.94, label: '> 6 000 pcs',    color: '#388E3C', bg: '#f0fdf4', barPct: 94 },
  ];
  const getLCTier = (qty) => {
    const q = parseFloat(qty) || 0;
    return LC_TIERS.find(t => q <= t.maxQty) || LC_TIERS[LC_TIERS.length - 1];
  };
  const getFactoryPeakEff = (zone) => {
    // 1. Use zone's own stored peakEff if available
    if (zone?.peakEff && parseFloat(zone.peakEff) > 0) return parseFloat(zone.peakEff);
    // 2. Fallback: matching garment supplier from Sourcing module
    const sups = (safeDb.suppliers || []).filter(s => s.supplierType === 'Garment Supplier');
    const match = sups.find(s => (s.address?.country || '').toLowerCase() === (zone?.country || '').toLowerCase());
    if (match && parseFloat(match.factoryEff) > 0) return parseFloat(match.factoryEff);
    const effs = sups.map(s => parseFloat(s.factoryEff || 0)).filter(e => e > 0);
    return effs.length > 0 ? effs.reduce((a, b) => a + b, 0) / effs.length : 85;
  };
  const computeZoneCM = (zone, smv, qty) => {
    const cpm100  = parseFloat(zone.cpm);
    const peakEff = getFactoryPeakEff(zone);
    // Use zone's own LC factor for this tier if defined, else use global default
    const tierIdx = LC_TIERS.findIndex(t => (parseFloat(qty) || 0) <= t.maxQty);
    const safeTierIdx = tierIdx === -1 ? LC_TIERS.length - 1 : tierIdx;
    const globalTier  = LC_TIERS[safeTierIdx];
    // zone.lcFactors overrides the default factor for this tier
    const zoneFactor  = zone.lcFactors?.[safeTierIdx];
    const lcFactor    = (zoneFactor !== undefined && !isNaN(parseFloat(zoneFactor)))
      ? parseFloat(zoneFactor)
      : globalTier.factor;
    const tier    = { ...globalTier, factor: lcFactor }; // merged tier with zone-specific factor
    const achEff  = Math.min(peakEff * lcFactor, 99);
    const cm      = smv > 0 && cpm100 > 0 ? (smv * cpm100) / (achEff / 100) : 0;
    return { cpm100, peakEff: parseFloat(peakEff.toFixed(1)), tier, lcFactor, achEff: parseFloat(achEff.toFixed(1)), cm, usedZoneLC: zoneFactor !== undefined };
  };
  const toggleZone = (zoneId) =>
    setSelectedZones(prev => prev.includes(zoneId) ? prev.filter(id => id !== zoneId) : [...prev, zoneId]);
  const lockZone = (zone, bd) => {
    setWs(prev => ({ ...prev, cm: { ...prev.cm, cpm: bd.cpm100, eff: bd.achEff, cost: 0, zoneId: zone.id, zoneName: zone.name } }));
  };

  const calculateLineCost = (item) => {
    let base = (parseFloat(item.con) || 0) * (parseFloat(item.price) || 0);
    let waste = 0;
    if (activeBlock === 'MATERIAL') waste = (parseFloat(item.wasteCut) || 0) + (parseFloat(item.wasteFin) || 0);
    else waste = parseFloat(item.waste) || 0;
    return base * (1 + waste / 100);
  };

  const handle = (k, v) => setTIn((p) => ({ ...p, [k]: v }));

  const handlePriceInput = (val) => {
    const floatVal = parseFloat(val);
    handle('price', isNaN(floatVal) ? '' : floatVal / conversionFactor);
  };
  const getDisplayPrice = () => tIn.price ? (parseFloat(tIn.price) * conversionFactor).toFixed(3) : '';

  const applySourcePrice = (quote) => {
    const rawPrice = parseFloat(quote.price || 0) / conversionFactor;
    setTIn(prev => ({ ...prev, price: rawPrice, supplier: quote.supplier || prev.supplier || '', item: prev.item || quote.item || '' }));
  };

  const addOrUpdateItem = () => {
    const cat = activeBlock.toLowerCase();
    if (!tIn.item) return alert('Please select or enter an Item Name.');
    const calculatedTotal = calculateLineCost(tIn).toFixed(3);
    const finalItem = { ...tIn, total: calculatedTotal };
    if (editId) {
      setWs((prev) => ({ ...prev, [cat]: prev[cat].map((x) => x.id === editId ? { ...finalItem, id: editId } : x) }));
      setEditId(null);
    } else {
      setWs((prev) => ({ ...prev, [cat]: [...prev[cat], { ...finalItem, id: Date.now() }] }));
    }
    setTIn({});
  };

  const handleEdit   = (item) => { setTIn(item); setEditId(item.id); };
  const cancelEdit   = () => { setTIn({}); setEditId(null); };

  const currentTotalBase = calculateLineCost(tIn);
  const currentTotalDisp = (currentTotalBase * conversionFactor).toFixed(3);

  // ── Sub-components (preserved) ──
  const Header = () => (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px' }}>
      <div style={{ display:'flex', gap:'5px', overflowX:'auto', paddingBottom:'5px' }}>
        {['DASHBOARD','MATERIAL','TRIMS','VA','FINISHING','CM','SUMMARY'].map((n) => (
          <button key={n} style={{ padding:'8px 12px', background:activeBlock===n?'#1565C0':'#CED4DA', color:activeBlock===n?'white':'#212529', border:'none', borderRadius:'4px', cursor:'pointer', fontWeight:'600', fontSize:'0.85rem' }} onClick={()=>setActiveBlock(n)}>
            {n}
          </button>
        ))}
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:'15px' }}>
        {currentStyle && (
          <div style={{ fontSize:'1rem', color:'#1565C0', fontWeight:'bold', cursor:'pointer', textDecoration:'underline' }} onClick={()=>openStylePassport(currentStyle)}>
            STYLE: {currentStyle.styleRef}
          </div>
        )}
        <button className="btn-p" style={{ background:'#2E7D32', padding:'8px 15px' }} onClick={() => {
            // Auto-stamp current SMV into smvHistory on every save (for CM block tracking)
            const currentSam = parseFloat(ws.cm?.sam);
            if (currentSam > 0) {
              const existing = ws.cm?.smvHistory || [];
              const alreadyLogged = existing.some(h => Math.abs(parseFloat(h.smv) - currentSam) < 0.001);
              if (!alreadyLogged) {
                const entry = {
                  smv: currentSam,
                  source: workstationSMV > 0 && Math.abs(currentSam - workstationSMV) < 0.01 ? 'workstation' : 'manual',
                  date: new Date().toISOString().split('T')[0],
                  note: 'Saved in CM costing'
                };
                setWs(prev => {
                  const updatedWs = { ...prev, cm: { ...prev.cm, smvHistory: [...existing, entry] } };
                  setTimeout(() => saveCosting(), 50); // save after state update
                  return updatedWs;
                });
                return;
              }
            }
            saveCosting();
          }}>SAVE COSTING</button>
      </div>
    </div>
  );

  const ActionButtons = ({ label }) => (
    <div style={{ marginTop:'20px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
      <div className="badge">Projected Line Cost: {safeCurrency.name} {currentTotalDisp}</div>
      <div style={{ display:'flex', gap:'10px' }}>
        {editId && <button className="btn-del" onClick={cancelEdit} style={{ background:'#6C757D' }}>CANCEL</button>}
        <button className="btn-p" onClick={addOrUpdateItem} style={{ background:editId?'#FF8F00':'#1565C0' }}>{editId ? 'UPDATE LINE' : label}</button>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────
  // MATERIAL BLOCK
  // ─────────────────────────────────────────────────────────────────
  if (activeBlock === 'MATERIAL') {
    return (
      <div className="layout-master" style={{ display:'block' }}>
        <Header />

        {/* Sourcing Reference Prices */}
        <SourcingRefPanel
          db={db} blockKey="Fabric"
          conversionFactor={conversionFactor} currencyName={safeCurrency.name}
          onUsePrice={applySourcePrice}
        />

        <div className="card" style={{ border:editId?'2px solid #FF8F00':'1px solid #DEE2E6', padding:'20px' }}>
          <h4>{editId ? 'EDITING FABRIC LINE' : `FABRIC COSTING (VALUES IN ${safeCurrency.name})`}</h4>
          <div className="grid-4" style={{gap:'20px'}}>
            <div className="field">
              <label style={{ color:'#1565C0', fontWeight:'bold' }}>Fabric Name (From Admin)</label>
              <select value={tIn.item||''} onChange={(e)=>handle('item',e.target.value)} style={{ borderColor:'#1565C0', background:'#E3F2FD' }}>
                <option value="">-- Select Fabric --</option>
                {(safeDb.fabrics||[]).map((f)=><option key={f.id} value={f.name}>{f.name}</option>)}
              </select>
            </div>
            <div className="field"><label>Construction</label><input value={tIn.construction||''} onChange={(e)=>handle('construction',e.target.value)} placeholder="60x60 / 20x20"/></div>
            <div className="field"><label>Content</label><input value={tIn.content||''} onChange={(e)=>handle('content',e.target.value)} placeholder="Cotton/Poly"/></div>
            <div className="field"><label>Width (Inches)</label><input value={tIn.width||''} onChange={(e)=>handle('width',e.target.value)}/></div>
            <div className="field">
              <label style={{ color: tIn.supplier&&(db?.ws?.sourcingPrices||[]).some(p=>p.supplier===tIn.supplier)?'#388E3C':'inherit' }}>
                Supplier {tIn.supplier&&(db?.ws?.sourcingPrices||[]).some(p=>p.supplier===tIn.supplier)?'✓ (Sourcing)':''}
              </label>
              <input value={tIn.supplier||''} onChange={(e)=>handle('supplier',e.target.value)}/>
            </div>
            <div className="field">
              <label>{rateLabel}</label>
              <input type="number" value={getDisplayPrice()} onChange={(e)=>handlePriceInput(e.target.value)}
                style={{ borderColor: tIn.price?'#1565C0':'#CED4DA', background: tIn.price?'#E3F2FD':'white', fontWeight: tIn.price?'bold':'normal' }}/>
            </div>
            <div className="field">
              <label style={{ display:'flex', alignItems:'center', gap:'5px' }}>
                Consumption
                {markerData?.consumptionM > 0 && (
                  <span style={{ fontSize:'9px', background:'#f3e8ff', color:'#6A1B9A', padding:'1px 6px', borderRadius:'4px', fontWeight:'800', cursor:'pointer' }}
                    title={`Marker consumption: ${markerData.consumptionM} m/garment at ${markerData.fabricWidth}cm fabric width — click to restore`}
                    onClick={() => handle('con', markerData.consumptionM)}>
                    MK: {markerData.consumptionM}m ↺
                  </span>
                )}
              </label>
              <input type="number" value={tIn.con||''} onChange={(e)=>handle('con',e.target.value)}/>
            </div>
            <div className="field">
              <label style={{ display:'flex', alignItems:'center', gap:'5px' }}>
                Waste Cut%
                {markerData?.wastagePct > 0 && (
                  <span style={{ fontSize:'9px', background:'#f3e8ff', color:'#6A1B9A', padding:'1px 6px', borderRadius:'4px', fontWeight:'800', cursor:'pointer' }}
                    title={`Actual wastage from Marker Maker (${markerData.yieldPct}% utilisation, saved ${new Date(markerData.savedOn).toLocaleString()}) — click to restore`}
                    onClick={() => handle('wasteCut', markerData.wastagePct)}>
                    MK: {markerData.wastagePct}% ↺
                  </span>
                )}
              </label>
              <input type="number" value={tIn.wasteCut||''} onChange={(e)=>handle('wasteCut',e.target.value)}/>
            </div>
            <div className="field"><label>Waste Fin%</label><input type="number" value={tIn.wasteFin||''} onChange={(e)=>handle('wasteFin',e.target.value)}/></div>
          </div>
          {markerData && (
            <div style={{ fontSize:'10px', color:'#6A1B9A', marginTop:'-8px', marginBottom:'12px' }}>
              📐 Marker Maker has real nesting data for this style — {markerData.totalGarments} garments, {markerData.usedLength}cm used, {markerData.yieldPct}% utilisation. Click the "MK:" badges above to use it instead of a manual estimate.
            </div>
          )}
          <ActionButtons label="ADD FABRIC LINE"/>
        </div>
        <br/>
        <CustomTable data={ws.material} cols={['item','construction','width','supplier','price','con','total']} onDelete={(id)=>setWs({...ws, material:ws.material.filter((x)=>x.id!==id)})} onEdit={handleEdit}/>
      </div>
    );
  }
// ═══════════════════════════════════════════════════════════════════
// COSTINGWORKSHEETS — CW-SPLIT 1
// TRIMS + VA + FINISHING blocks
// Paste immediately after BASE FILE
// ═══════════════════════════════════════════════════════════════════

  // ─────────────────────────────────────────────────────────────────
  // TRIMS BLOCK
  // ─────────────────────────────────────────────────────────────────
  if (activeBlock === 'TRIMS') {
    return (
      <div className="layout-master" style={{ display:'block' }}>
        <Header />

        <SourcingRefPanel
          db={db} blockKey="Trims"
          conversionFactor={conversionFactor} currencyName={safeCurrency.name}
          onUsePrice={applySourcePrice}
        />

        <div className="card" style={{ border:editId?'2px solid #FF8F00':'1px solid #DEE2E6', padding:'20px' }}>
          <h4>{editId ? 'EDITING TRIM LINE' : `TRIMS COSTING (VALUES IN ${safeCurrency.name})`}</h4>
          <div className="grid-4" style={{gap:'20px'}}>
            <div className="field">
              <label style={{ color:'#1565C0', fontWeight:'bold' }}>Trim Item (From Admin)</label>
              <select value={tIn.item||''} onChange={(e)=>handle('item',e.target.value)} style={{ borderColor:'#1565C0', background:'#E3F2FD' }}>
                <option value="">-- Select Trim --</option>
                {(safeDb.trimTypes||[]).map((t)=><option key={t.id} value={t.name}>{t.name}</option>)}
              </select>
            </div>
            <div className="field"><label>Ref / Desc</label><input value={tIn.desc||''} onChange={(e)=>handle('desc',e.target.value)}/></div>
            <div className="field">
              <label style={{ color: tIn.supplier&&(db?.ws?.sourcingPrices||[]).some(p=>p.supplier===tIn.supplier)?'#388E3C':'inherit' }}>
                Supplier {tIn.supplier&&(db?.ws?.sourcingPrices||[]).some(p=>p.supplier===tIn.supplier)?'✓ (Sourcing)':''}
              </label>
              <input value={tIn.supplier||''} onChange={(e)=>handle('supplier',e.target.value)}/>
            </div>
            <div className="field">
              <label style={{ color:'#1565C0', fontWeight:'bold' }}>UOM</label>
              <select value={tIn.uom||''} onChange={(e)=>handle('uom',e.target.value)} style={{ borderColor:'#1565C0', background:'#E3F2FD' }}>
                <option value="">-Select-</option>
                {(safeDb.uoms||[]).map((u)=><option key={u.id} value={u.name}>{u.name}</option>)}
                {(!safeDb.uoms||safeDb.uoms.length===0) && <><option>PCS</option><option>MTR</option></>}
              </select>
            </div>
            <div className="field">
              <label>{rateLabel}</label>
              <input type="number" value={getDisplayPrice()} onChange={(e)=>handlePriceInput(e.target.value)}
                style={{ borderColor:tIn.price?'#1565C0':'#CED4DA', background:tIn.price?'#E3F2FD':'white', fontWeight:tIn.price?'bold':'normal' }}/>
            </div>
            <div className="field"><label>Consumption</label><input type="number" value={tIn.con||''} onChange={(e)=>handle('con',e.target.value)}/></div>
            <div className="field"><label>Wastage %</label><input type="number" value={tIn.waste||''} onChange={(e)=>handle('waste',e.target.value)}/></div>
          </div>
          <ActionButtons label="ADD TRIM LINE"/>
        </div>
        <br/>
        <CustomTable data={ws.trims} cols={['item','desc','uom','supplier','price','con','waste','total']} onDelete={(id)=>setWs({...ws, trims:ws.trims.filter((x)=>x.id!==id)})} onEdit={handleEdit}/>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────
  // VA BLOCK
  // ─────────────────────────────────────────────────────────────────
  if (activeBlock === 'VA') {
    return (
      <div className="layout-master" style={{ display:'block' }}>
        <Header />

        <SourcingRefPanel
          db={db} blockKey="Embellishment"
          conversionFactor={conversionFactor} currencyName={safeCurrency.name}
          onUsePrice={applySourcePrice}
        />

        <div className="card" style={{ border:editId?'2px solid #FF8F00':'1px solid #DEE2E6', padding:'20px' }}>
          <h4>{editId ? 'EDITING PROCESS' : `VA / PROCESS COSTING (VALUES IN ${safeCurrency.name})`}</h4>
          <div className="grid-4" style={{gap:'20px'}}>
            <div className="field">
              <label style={{ color:'#1565C0', fontWeight:'bold' }}>Process Type (From Admin)</label>
              <select value={tIn.item||''} onChange={(e)=>handle('item',e.target.value)} style={{ borderColor:'#1565C0', background:'#E3F2FD' }}>
                <option value="">-- Select Process --</option>
                {(safeDb.vaProcesses||[]).map((p)=><option key={p.id} value={p.name}>{p.name}</option>)}
              </select>
            </div>
            <div className="field"><label>Sub-Process / Details</label><input value={tIn.sub||''} onChange={(e)=>handle('sub',e.target.value)}/></div>
            <div className="field">
              <label style={{ color: tIn.supplier&&(db?.ws?.sourcingPrices||[]).some(p=>p.supplier===tIn.supplier)?'#388E3C':'inherit' }}>
                Supplier {tIn.supplier&&(db?.ws?.sourcingPrices||[]).some(p=>p.supplier===tIn.supplier)?'✓ (Sourcing)':''}
              </label>
              <input value={tIn.supplier||''} onChange={(e)=>handle('supplier',e.target.value)}/>
            </div>
            <div className="field">
              <label>{rateLabel}</label>
              <input type="number" value={getDisplayPrice()} onChange={(e)=>handlePriceInput(e.target.value)}
                style={{ borderColor:tIn.price?'#1565C0':'#CED4DA', background:tIn.price?'#E3F2FD':'white', fontWeight:tIn.price?'bold':'normal' }}/>
            </div>
            <div className="field"><label>Consumption</label><input type="number" value={tIn.con||''} onChange={(e)=>handle('con',e.target.value)} placeholder="Usually 1"/></div>
            <div className="field"><label>Wastage %</label><input type="number" value={tIn.waste||''} onChange={(e)=>handle('waste',e.target.value)}/></div>
          </div>
          <ActionButtons label="ADD VA PROCESS"/>
        </div>
        <br/>
        <CustomTable data={ws.va} cols={['item','sub','supplier','price','con','total']} onDelete={(id)=>setWs({...ws, va:ws.va.filter((x)=>x.id!==id)})} onEdit={handleEdit}/>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────
  // FINISHING BLOCK
  // ─────────────────────────────────────────────────────────────────
  if (activeBlock === 'FINISHING') {
    return (
      <div className="layout-master" style={{ display:'block' }}>
        <Header />

        <SourcingRefPanel
          db={db} blockKey="Packaging"
          conversionFactor={conversionFactor} currencyName={safeCurrency.name}
          onUsePrice={applySourcePrice}
        />

        <div className="card" style={{ border:editId?'2px solid #FF8F00':'1px solid #DEE2E6', padding:'20px' }}>
          <h4>{editId ? 'EDITING ITEM' : `FINISHING & PACKING COSTING (VALUES IN ${safeCurrency.name})`}</h4>
          <div className="grid-4" style={{gap:'20px'}}>
            <div className="field"><label>Item / Activity</label><input value={tIn.item||''} onChange={(e)=>handle('item',e.target.value)} placeholder="Polybag, Tagging, Folding..."/></div>
            <div className="field"><label>Description</label><input value={tIn.desc||''} onChange={(e)=>handle('desc',e.target.value)}/></div>
            <div className="field">
              <label>{rateLabel}</label>
              <input type="number" value={getDisplayPrice()} onChange={(e)=>handlePriceInput(e.target.value)}
                style={{ borderColor:tIn.price?'#1565C0':'#CED4DA', background:tIn.price?'#E3F2FD':'white', fontWeight:tIn.price?'bold':'normal' }}/>
            </div>
            <div className="field"><label>Consumption</label><input type="number" value={tIn.con||''} onChange={(e)=>handle('con',e.target.value)}/></div>
          </div>
          <ActionButtons label="ADD FINISHING LINE"/>
        </div>
        <br/>
        <CustomTable data={ws.finishing} cols={['item','desc','price','con','total']} onDelete={(id)=>setWs({...ws, finishing:ws.finishing.filter((x)=>x.id!==id)})} onEdit={handleEdit}/>
      </div>
    );
  }
// ═══════════════════════════════════════════════════════════════════
// COSTINGWORKSHEETS — CW-SPLIT 2
// CM BLOCK + component close
// Paste immediately after CW-SPLIT 1 — THIS IS THE FINAL SPLIT
// ═══════════════════════════════════════════════════════════════════

  // ─────────────────────────────────────────────────────────────────
  // CM BLOCK
  // ─────────────────────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────
  // CM BLOCK — ZONE COMPARISON TOOL
  // ─────────────────────────────────────────────────────────────────
  if (activeBlock === 'CM') {
    const isEstimated = !workstationSMV || Math.abs((parseFloat(ws.cm.sam)||0) - workstationSMV) > 0.01;
    const smv        = parseFloat(ws.cm.sam) || 0;
    // Order qty — read from ws first (user can override here), fallback to style
    const orderQty   = parseFloat(ws.cm.orderQty || currentStyle?.orderQty || currentStyle?.qty || currentStyle?.quantity || 0);
    const manualCM   = parseFloat(ws.cm.cost) || 0;
    const activeTier = getLCTier(orderQty);

    // Final CM using locked zone or manual
    const lockedCpm100 = parseFloat(ws.cm.cpm) || 0;
    const lockedEff    = parseFloat(ws.cm.eff)  || 1;
    const cmFormula    = lockedCpm100 > 0 ? (smv * lockedCpm100) / (lockedEff / 100) : 0;
    const cmFinal      = manualCM > 0 ? manualCM : cmFormula;

    // CM sourcing quotes panel
    const cmQuotes = (db?.supplierQuotations||[]).filter(q => {
      const i = (q.item||'').toLowerCase();
      return i.includes('cm')||i.includes('cut')||i.includes('make')||i.includes('sewing')||i.includes('stitch')||i.includes('labour');
    }).sort((a,b) => parseFloat(a.price)-parseFloat(b.price));
    const pushedCM    = (db?.ws?.sourcingPrices||[]).filter(p => p.block==='CM');
    const allCmPrices = [...pushedCM,...cmQuotes].filter((p,i,a) => a.findIndex(x=>x.id===p.id)===i);

    // Zones to show in comparison — those ticked
    const compareZones = (safeDb.productionZones||[]).filter(z => selectedZones.includes(z.id));

    return (
      <div className="layout-master" style={{ display:'block' }}>
        <Header />

        {/* ── Sourcing reference prices ── */}
        {allCmPrices.length > 0 && (
          <div style={{ marginBottom:'16px', padding:'12px 14px', background:'linear-gradient(135deg,#f0fdf4,#E3F2FD)', border:'1px solid #A5D6A7', borderRadius:'8px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'10px' }}>
              <div style={{ width:'8px', height:'8px', borderRadius:'50%', background:'#388E3C' }}/>
              <span style={{ fontSize:'11px', fontWeight:'800', color:'#15803d', textTransform:'uppercase', letterSpacing:'0.05em' }}>
                Sourcing Reference — CM / Labour Prices
              </span>
              <span style={{ marginLeft:'auto', fontSize:'10px', color:'#6C757D' }}>Click to use as manual CM cost</span>
            </div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:'8px' }}>
              {allCmPrices.map((p,i) => {
                const dp = (parseFloat(p.price||0)*conversionFactor).toFixed(3);
                const isBest = i===0;
                return (
                  <div key={p.id||i} onClick={()=>setWs(prev=>({...prev, cm:{...prev.cm, cost:parseFloat(p.price||0)/conversionFactor}}))}
                    style={{ padding:'8px 12px', background:'white', border:`1.5px solid ${isBest?'#388E3C':'#DEE2E6'}`, borderRadius:'8px', cursor:'pointer', minWidth:'130px', position:'relative' }}>
                    {isBest && <div style={{ position:'absolute', top:'-8px', right:'6px', background:'#388E3C', color:'white', fontSize:'8px', padding:'1px 6px', borderRadius:'8px', fontWeight:'800' }}>BEST</div>}
                    <div style={{ fontSize:'11px', fontWeight:'700', color:'#212529', marginBottom:'2px' }}>{p.item}</div>
                    <div style={{ fontSize:'10px', color:'#6C757D', marginBottom:'4px' }}>{p.supplier}</div>
                    <div style={{ fontSize:'15px', fontWeight:'800', color:isBest?'#388E3C':'#1565C0' }}>
                      {safeCurrency.name} {dp}
                      <span style={{ fontSize:'9px', fontWeight:'400', color:'#6C757D', marginLeft:'3px' }}>/{p.unit||'pcs'}</span>
                    </div>
                    {p.leadTime && <div style={{ fontSize:'9px', color:'#6C757D', marginTop:'2px' }}>Lead: {p.leadTime}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="card" style={{ padding:'20px' }}>

          {/* ── SMV History Panel — shown when history exists or manual SMV ── */}
          {(() => {
            const smvHist = ws.cm.smvHistory || [];
            const hasManual = isEstimated && parseFloat(ws.cm.sam) > 0;
            if (!hasManual && smvHist.length === 0) return null;
            return (
              <div style={{ marginBottom:'16px', padding:'12px 14px', background:'linear-gradient(135deg,#fefce8,#FFF8E1)', border:'1px solid #fde68a', borderRadius:'8px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' }}>
                  <span style={{ fontSize:'11px', fontWeight:'800', color:'#92400e', textTransform:'uppercase', letterSpacing:'0.05em' }}>
                    📋 SMV — Costing History for {currentStyle?.styleRef || 'this style'}
                  </span>
                  {hasManual && (
                    <button onClick={() => {
                      const entry = {
                        smv: parseFloat(ws.cm.sam),
                        source: 'manual',
                        date: new Date().toISOString().split('T')[0],
                        note: `Manual entry in CM costing`
                      };
                      const updated = [...smvHist.filter(h => Math.abs(h.smv - entry.smv) > 0.001), entry];
                      setWs(prev => ({ ...prev, cm: { ...prev.cm, smvHistory: updated } }));
                    }} style={{ fontSize:'10px', padding:'3px 10px', background:'#FF8F00', color:'white', border:'none', borderRadius:'4px', cursor:'pointer', fontWeight:'800' }}>
                      + Log {parseFloat(ws.cm.sam).toFixed(3)} to History
                    </button>
                  )}
                </div>
                {smvHist.length > 0 ? (
                  <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', alignItems:'center' }}>
                    <span style={{ fontSize:'10px', color:'#92400e', fontWeight:'700' }}>Used:</span>
                    {smvHist.map((h, i) => {
                      const isActive = Math.abs(h.smv - parseFloat(ws.cm.sam)) < 0.001;
                      return (
                        <div key={i} title={`${h.date || ''}${h.note ? ' — ' + h.note : ''}`}
                          onClick={() => setWs(prev => ({...prev, cm:{...prev.cm, sam: h.smv}}))}
                          style={{ padding:'3px 10px', background: isActive ? '#FF8F00' : 'white', border:`1.5px solid ${isActive ? '#FF8F00' : '#fde68a'}`, borderRadius:'10px', cursor:'pointer', fontSize:'11px', fontWeight:isActive?'900':'600', color:isActive?'white':'#92400e', display:'flex', alignItems:'center', gap:'4px' }}>
                          {parseFloat(h.smv).toFixed(3)}
                          {h.source === 'workstation' ? ' 🔧' : ' ✎'}
                          {isActive && ' ◀'}
                        </div>
                      );
                    })}
                    <span style={{ fontSize:'9px', color:'#6C757D', marginLeft:'4px' }}>Click any to reuse</span>
                  </div>
                ) : (
                  <div style={{ fontSize:'10px', color:'#92400e', opacity:0.7 }}>No history yet — log the current SMV above to start tracking.</div>
                )}
              </div>
            );
          })()}

          {/* ── Row 1: SMV + Order Qty ── */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:'16px', marginBottom:'20px', alignItems:'end' }}>
            <div className="field">
              <label style={{ display:'flex', alignItems:'center', gap:'5px' }}>
                SMV (Minutes)
                {workstationSMV > 0 && (
                  <span style={{ fontSize:'9px', background:'#E8F5E9', color:'#166534', padding:'1px 6px', borderRadius:'4px', fontWeight:'800', cursor:'pointer' }}
                    title={`Workstation SMV: ${workstationSMV.toFixed(3)} — click to restore`}
                    onClick={() => setWs(prev => ({...prev, cm:{...prev.cm, sam: parseFloat(workstationSMV.toFixed(3))}}))}>
                    WS: {workstationSMV.toFixed(3)} ↺
                  </span>
                )}
              </label>
              <input type="number" step="0.001" value={ws.cm.sam}
                onChange={e => setWs({...ws, cm:{...ws.cm, sam:e.target.value}})}
                style={{ borderColor:isEstimated?'#C62828':'#166534', background:isEstimated?'#FFEBEE':'#f0fdf4', fontWeight:'bold', color:isEstimated?'#b91c1c':'#15803d' }}/>
              <div style={{ fontSize:'10px', marginTop:'3px', color:isEstimated?'#B71C1C':'#166534' }}>
                {isEstimated
                  ? workstationSMV > 0
                    ? `⚠ Modified from WS value ${workstationSMV.toFixed(3)}`
                    : '✎ Manual — workstation not completed'
                  : '✓ From Workstation'}
              </div>
            </div>

            <div className="field">
              <label style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                Order Quantity (pcs)
                <span style={{ fontSize:'9px', background:'#FFF8E1', color:'#92400e', padding:'1px 6px', borderRadius:'6px', fontWeight:'700' }}>
                  drives learning curve
                </span>
              </label>
              <input type="number" value={ws.cm.orderQty || orderQty || ''}
                onChange={e => setWs({...ws, cm:{...ws.cm, orderQty:e.target.value}})}
                placeholder="e.g. 1200"
                style={{ borderColor:'#FF8F00', background:'#FFF8E1', fontWeight:'bold', color:'#92400e' }}/>
              <div style={{ fontSize:'10px', marginTop:'3px', color:'#92400e' }}>
                {orderQty > 0
                  ? <span style={{ fontWeight:'bold', color:activeTier.color }}>
                      Tier: {activeTier.label} → LC factor {(activeTier.factor*100).toFixed(0)}%
                    </span>
                  : 'Enter qty to get learning curve estimate'}
              </div>
            </div>

            {/* Learning curve legend */}
            <div style={{ gridColumn:'span 2', background:'#F8F9FA', border:'1px solid #DEE2E6', borderRadius:'8px', padding:'10px 14px' }}>
              <div style={{ fontSize:'10px', fontWeight:'800', color:'#6C757D', marginBottom:'8px', textTransform:'uppercase' }}>
                Learning Curve Tiers
                <span style={{ fontWeight:'400', marginLeft:'6px', textTransform:'none', color:'#6C757D' }}>— configure per zone in Global Masters → Locations</span>
              </div>
              <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
                {LC_TIERS.map((t,i) => {
                  const isActive = orderQty > 0 && t === activeTier;
                  return (
                    <div key={i} style={{ padding:'3px 8px', borderRadius:'4px', fontSize:'10px', fontWeight:isActive?'900':'600',
                      background: isActive ? t.color : t.bg, color: isActive ? 'white' : t.color,
                      border:`1px solid ${t.color}`, transition:'all 0.2s' }}>
                      {t.label} → {(t.factor*100).toFixed(0)}%
                      {isActive && ' ◀'}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ── Row 2: Zone picker ── */}
          <div style={{ marginBottom:'20px' }}>
            <div style={{ fontSize:'11px', fontWeight:'800', color:'#0369a1', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'10px' }}>
              Select Zones to Compare
              {safeDb.productionZones.length === 0 && (
                <span style={{ marginLeft:'10px', fontSize:'10px', color:'#FF8F00', fontWeight:'400', textTransform:'none' }}>
                  ⚠ No zones defined — add them in Global Masters → Locations / CPM
                </span>
              )}
            </div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:'8px' }}>
              {(safeDb.productionZones||[]).map(z => {
                const checked  = selectedZones.includes(z.id);
                const isActive = ws.cm.zoneId === z.id;
                return (
                  <button key={z.id} onClick={() => toggleZone(z.id)}
                    style={{
                      padding:'8px 14px', borderRadius:'6px', cursor:'pointer', fontWeight:'700', fontSize:'12px',
                      border: isActive ? '2px solid #2E7D32' : checked ? '2px solid #1565C0' : '1px solid #DEE2E6',
                      background: isActive ? '#f0fdf4' : checked ? '#E3F2FD' : 'white',
                      color: isActive ? '#2E7D32' : checked ? '#1565C0' : '#6C757D',
                      display:'flex', alignItems:'center', gap:'6px',
                    }}>
                    {isActive ? '★' : checked ? '✓' : '○'}
                    {z.name}
                    <span style={{ fontSize:'10px', fontWeight:'400', color:'#6C757D' }}>
                      {z.country}{z.region ? ` · ${z.region}` : ''} · {z.cpm}
                    </span>
                    {isActive && <span style={{ fontSize:'9px', background:'#2E7D32', color:'white', padding:'1px 5px', borderRadius:'4px' }}>ACTIVE</span>}
                  </button>
                );
              })}
              {/* Manual entry option */}
              <button onClick={() => {
                  setWs(prev => ({...prev, cm:{...prev.cm, zoneId:null, zoneName:'Manual'}}));
                  setSelectedZones([]);
                }}
                style={{ padding:'8px 14px', borderRadius:'6px', cursor:'pointer', fontWeight:'700', fontSize:'12px',
                  border: ws.cm.zoneName==='Manual' ? '2px solid #FF8F00' : '1px dashed #CED4DA',
                  background: ws.cm.zoneName==='Manual' ? '#FFF8E1' : 'white',
                  color: ws.cm.zoneName==='Manual' ? '#92400e' : '#6C757D' }}>
                ✎ Manual CPM
              </button>
            </div>
          </div>

          {/* ── Row 3: Comparison Cards ── */}
          {compareZones.length > 0 && (
            <div style={{ marginBottom:'20px' }}>
              <div style={{ fontSize:'11px', fontWeight:'800', color:'#212529', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'12px' }}>
                Zone Comparison — {safeCurrency.name} · SMV {smv.toFixed(3)} · {orderQty > 0 ? `${Number(orderQty).toLocaleString()} pcs` : 'qty not set'}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:`repeat(${Math.min(compareZones.length, 4)}, 1fr)`, gap:'14px' }}>
                {compareZones.map(zone => {
                  const bd        = computeZoneCM(zone, smv, orderQty);
                  const isActive  = ws.cm.zoneId === zone.id;
                  const cmDisp    = (bd.cm * conversionFactor).toFixed(3);
                  const cpmDisp   = (bd.cpm100 * conversionFactor).toFixed(4);
                  const effCpmDisp = bd.achEff > 0 ? (bd.cpm100 / (bd.achEff/100) * conversionFactor).toFixed(4) : '—';

                  // Sort rank: cheapest = rank 1
                  const allCMs    = compareZones.map(z => computeZoneCM(z, smv, orderQty).cm);
                  const sortedCMs = [...allCMs].sort((a,b) => a-b);
                  const rank      = sortedCMs.indexOf(bd.cm) + 1;
                  const isCheapest = rank === 1;

                  return (
                    <div key={zone.id} style={{
                      border: isActive ? '2px solid #2E7D32' : isCheapest ? '2px solid #1565C0' : '1px solid #DEE2E6',
                      borderRadius:'10px', overflow:'hidden', position:'relative',
                      boxShadow: isActive ? '0 4px 16px rgba(5,150,105,0.15)' : isCheapest ? '0 4px 16px rgba(37,99,235,0.1)' : '0 1px 4px rgba(0,0,0,0.04)',
                    }}>
                      {/* Card header */}
                      <div style={{ background: isActive ? '#2E7D32' : isCheapest ? '#1565C0' : '#F8F9FA',
                        padding:'10px 14px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <div>
                          <div style={{ fontWeight:'800', fontSize:'13px', color: isActive||isCheapest ? 'white' : '#212529' }}>
                            {zone.name}
                          </div>
                          <div style={{ fontSize:'10px', color: isActive||isCheapest ? 'rgba(255,255,255,0.7)' : '#6C757D', marginTop:'1px' }}>
                            {zone.country}{zone.region ? ` · ${zone.region}` : ''}
                          </div>
                        </div>
                        {isActive && <span style={{ fontSize:'10px', background:'rgba(255,255,255,0.2)', color:'white', padding:'2px 8px', borderRadius:'10px', fontWeight:'800' }}>★ ACTIVE</span>}
                        {!isActive && isCheapest && <span style={{ fontSize:'10px', background:'rgba(255,255,255,0.2)', color:'white', padding:'2px 8px', borderRadius:'10px', fontWeight:'800' }}>LOWEST CM</span>}
                        {!isActive && !isCheapest && <span style={{ fontSize:'10px', background:'#DEE2E6', color:'#6C757D', padding:'2px 8px', borderRadius:'10px', fontWeight:'700' }}>#{rank}</span>}
                      </div>

                      {/* Body */}
                      <div style={{ padding:'14px', background:'white' }}>

                        {/* CPM @ 100% */}
                        <div style={{ marginBottom:'10px', paddingBottom:'10px', borderBottom:'1px solid #F4F6F8' }}>
                          <div style={{ fontSize:'10px', color:'#6C757D', fontWeight:'700', marginBottom:'3px', textTransform:'uppercase' }}>CPM @ 100% eff</div>
                          <div style={{ fontSize:'18px', fontWeight:'900', color:'#212529' }}>{cpmDisp}</div>
                          <div style={{ fontSize:'10px', color:'#6C757D' }}>{safeCurrency.name} per minute</div>
                        </div>

                        {/* Factory Peak Eff */}
                        <div style={{ marginBottom:'10px', paddingBottom:'10px', borderBottom:'1px solid #F4F6F8', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                          <div>
                            <div style={{ fontSize:'10px', color:'#6C757D', fontWeight:'700', marginBottom:'2px', textTransform:'uppercase' }}>Factory Peak</div>
                            <div style={{ fontSize:'16px', fontWeight:'800', color:'#212529' }}>{bd.peakEff}%</div>
                            <div style={{ fontSize:'9px', color:'#6C757D' }}>from supplier data</div>
                          </div>
                          <div>
                            <div style={{ fontSize:'10px', color:'#6C757D', fontWeight:'700', marginBottom:'2px', textTransform:'uppercase' }}>LC Factor</div>
                            <div style={{ fontSize:'16px', fontWeight:'800', color:bd.tier.color }}>{(bd.tier.factor*100).toFixed(0)}%</div>
                            <div style={{ fontSize:'9px', color:bd.tier.color, fontWeight:'700' }}>{bd.tier.label}</div>
                          </div>
                        </div>

                        {/* Achievable Eff */}
                        <div style={{ marginBottom:'10px', paddingBottom:'10px', borderBottom:'1px solid #F4F6F8' }}>
                          <div style={{ fontSize:'10px', color:'#6C757D', fontWeight:'700', marginBottom:'4px', textTransform:'uppercase' }}>
                            Achievable Efficiency = Peak × LC
                          </div>
                          <div style={{ fontSize:'13px', fontFamily:'monospace', color:'#6C757D', marginBottom:'4px' }}>
                            {bd.peakEff}% × {(bd.lcFactor*100).toFixed(0)}% = <strong style={{ color:'#7B1FA2' }}>{bd.achEff}%</strong>
                          </div>
                          {bd.usedZoneLC && (
                            <div style={{ fontSize:'9px', background:'#F3E5F5', color:'#7B1FA2', padding:'1px 6px', borderRadius:'4px', display:'inline-block', fontWeight:'700', marginBottom:'4px' }}>
                              ✎ Zone-specific LC factor
                            </div>
                          )}
                          {/* Efficiency bar */}
                          <div style={{ height:'7px', background:'#F4F6F8', borderRadius:'4px', overflow:'hidden' }}>
                            <div style={{ height:'100%', width:`${bd.achEff}%`, background:`linear-gradient(90deg,${bd.tier.color},#7B1FA2)`, borderRadius:'4px', transition:'width 0.4s' }}/>
                          </div>
                          <div style={{ fontSize:'9px', color:'#6C757D', marginTop:'3px' }}>Eff CPM at {bd.achEff}% = {effCpmDisp}</div>
                        </div>

                        {/* CM Result */}
                        <div style={{ marginBottom:'12px' }}>
                          <div style={{ fontSize:'10px', color:'#6C757D', fontWeight:'700', marginBottom:'2px', textTransform:'uppercase' }}>
                            Calculated CM
                          </div>
                          <div style={{ fontSize:'10px', fontFamily:'monospace', color:'#6C757D', marginBottom:'4px' }}>
                            {smv.toFixed(3)} × {cpmDisp} ÷ {bd.achEff/100}
                          </div>
                          <div style={{ fontSize:'24px', fontWeight:'900', color: isCheapest||isActive ? '#2E7D32' : '#212529' }}>
                            {safeCurrency.name} {cmDisp}
                          </div>
                          {compareZones.length > 1 && !isCheapest && (
                            <div style={{ fontSize:'10px', color:'#C62828', marginTop:'2px' }}>
                              +{safeCurrency.name} {((bd.cm - sortedCMs[0]) * conversionFactor).toFixed(3)} vs cheapest
                            </div>
                          )}
                        </div>

                        {/* Lock button */}
                        <button onClick={() => lockZone(zone, bd)}
                          style={{ width:'100%', padding:'9px', fontWeight:'bold', fontSize:'12px', cursor:'pointer', borderRadius:'6px', border:'none', transition:'all 0.15s',
                            background: isActive ? '#2E7D32' : '#212529',
                            color:'white' }}>
                          {isActive ? '★ Active Zone' : '→ Use This Zone'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Row 4: Fine-tune active zone ── */}
          <div style={{ background:'#F8F9FA', border: ws.cm.zoneId||ws.cm.zoneName ? '2px solid #DEE2E6' : '2px dashed #CED4DA', borderRadius:'10px', padding:'16px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px' }}>
              <div style={{ fontSize:'12px', fontWeight:'800', color:'#212529', textTransform:'uppercase', letterSpacing:'0.04em' }}>
                {ws.cm.zoneName && ws.cm.zoneName !== 'Manual'
                  ? `Fine-tune: ${ws.cm.zoneName}`
                  : ws.cm.zoneName === 'Manual'
                    ? 'Manual CPM Entry'
                    : 'No zone selected — enter CPM manually'}
              </div>
              {manualCM > 0 && (
                <button onClick={() => setWs(prev=>({...prev, cm:{...prev.cm, cost:0}}))}
                  style={{ fontSize:'10px', color:'#B71C1C', background:'#FFEBEE', border:'1px solid #fecaca', borderRadius:'4px', padding:'3px 8px', cursor:'pointer', fontWeight:'700' }}>
                  ✕ Clear manual override
                </button>
              )}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:'14px', alignItems:'start' }}>

              {/* CPM @ 100% — editable */}
              <div className="field">
                <label>CPM @ 100% ({safeCurrency.name})
                  {ws.cm.zoneName === 'Manual' && <span style={{ marginLeft:'5px', fontSize:'9px', background:'#FFF8E1', color:'#92400e', padding:'1px 5px', borderRadius:'4px' }}>manual</span>}
                </label>
                <input type="number" step="0.0001"
                  value={(parseFloat(ws.cm.cpm||0)*conversionFactor).toFixed(4)}
                  onChange={e => {
                    const base = parseFloat(e.target.value) / conversionFactor;
                    setWs(prev => ({...prev, cm:{...prev.cm, cpm: isNaN(base)?0:base, zoneId: null, zoneName:'Manual'}}));
                  }}
                  placeholder="0.0000"
                  style={{ fontWeight:'bold', borderColor: ws.cm.zoneName==='Manual'?'#FF8F00':ws.cm.zoneName?'#0284c7':'#1565C0',
                    background: ws.cm.zoneName==='Manual'?'#FFF8E1':ws.cm.zoneName?'#f0f9ff':'#E3F2FD' }}/>
                <div style={{ fontSize:'10px', marginTop:'2px', color:'#6C757D' }}>
                  {lockedCpm100>0 && lockedEff>0 ? `Eff CPM @${lockedEff}% = ${(lockedCpm100/(lockedEff/100)*conversionFactor).toFixed(4)}` : 'enter value above'}
                </div>
              </div>

              {/* Efficiency — override the LC suggestion */}
              <div className="field">
                <label>Efficiency % (adjust)
                  {ws.cm.zoneId && orderQty > 0 && (
                    <span style={{ marginLeft:'4px', fontSize:'9px', color:'#7B1FA2', fontWeight:'700' }}>
                      LC: {computeZoneCM((safeDb.productionZones||[]).find(z=>z.id===ws.cm.zoneId)||{cpm:0}, smv, orderQty).achEff}%
                    </span>
                  )}
                </label>
                <input type="number" min="1" max="100" step="0.5" value={ws.cm.eff}
                  onChange={e => setWs({...ws, cm:{...ws.cm, eff:e.target.value}})}
                  style={{ fontWeight:'bold', borderColor:'#7B1FA2', background:'#faf5ff', color:'#4c1d95' }}/>
                <div style={{ fontSize:'10px', marginTop:'2px', color:'#7B1FA2' }}>lower eff → higher CM cost</div>
              </div>

              {/* Manual CM override */}
              <div className="field">
                <label style={{ color: manualCM>0?'#B71C1C':'#6C757D' }}>
                  Manual CM Override ({safeCurrency.name})
                  {manualCM>0 && <span style={{ marginLeft:'4px', fontSize:'9px', background:'#FFCDD2', color:'#B71C1C', padding:'1px 5px', borderRadius:'4px', fontWeight:'800' }}>ACTIVE</span>}
                </label>
                <input type="number" value={manualCM>0 ? (manualCM*conversionFactor).toFixed(3) : ''}
                  onChange={e => { const base=parseFloat(e.target.value)/conversionFactor; setWs(prev=>({...prev, cm:{...prev.cm, cost:isNaN(base)?0:base}})); }}
                  placeholder="leave blank — use formula"
                  style={{ borderColor:manualCM>0?'#B71C1C':'#CED4DA', background:manualCM>0?'#FFEBEE':'white' }}/>
              </div>

              {/* Result */}
              <div style={{ background: manualCM>0?'#FFEBEE':'#f0fdf4', border:`2px solid ${manualCM>0?'#fca5a5':'#86efac'}`, borderRadius:'8px', padding:'12px', textAlign:'center' }}>
                <div style={{ fontSize:'10px', color:'#6C757D', fontWeight:'700', marginBottom:'6px', textTransform:'uppercase' }}>
                  {manualCM>0 ? 'Manual Override' : 'Calculated CM'}
                </div>
                <div style={{ fontSize:'1.6rem', fontWeight:'900', color:manualCM>0?'#B71C1C':'#2E7D32', lineHeight:1 }}>
                  {safeCurrency.name} {(cmFinal * conversionFactor).toFixed(3)}
                </div>
                {manualCM>0 && <div style={{ fontSize:'10px', color:'#6C757D', marginTop:'4px' }}>formula: {(cmFormula*conversionFactor).toFixed(3)}</div>}
                <div style={{ fontSize:'10px', fontFamily:'monospace', color:'#6C757D', marginTop:'4px' }}>
                  {smv.toFixed(3)} × {(lockedCpm100*conversionFactor).toFixed(4)} ÷ {lockedEff/100}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    );
  }

  return null;
}
